import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGlobalState } from '../../utils/GlobalState';
import { ArrowLeft } from "lucide-react";
import { fetchIssueTypes, submitDisagreeIssueDetection } from "../../services/apiService";
import Swal from "sweetalert2";
import { decodedToken, getAuthToken } from "../../utils/auth";

const generateRectHash = (rect) => {
  return `${rect.muraType}|${rect.x.toFixed(2)}|${rect.y.toFixed(2)}|${rect.width.toFixed(2)}|${rect.height.toFixed(2)}`;
};

const getUserType = () => {
  try {
    const token = getAuthToken();
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType?.toLowerCase() ?? null;
  } catch {
    return null;
  }
};

const ReLabeling = () => {
  const isPowerAdmin = getUserType() === "poweradmin";
  const location = useLocation();
  const navigate = useNavigate();
  const { state: globalState, updateState } = useGlobalState();
  const decoded = decodedToken();
  const email = decoded?.email || '';
  const companyInfo = typeof decoded?.companyInfo === 'string' && decoded.companyInfo.trim() !== ''
    ? decoded.companyInfo.trim() : '';

  const {
    suggestion: incomingSuggestion = "",
    imageSrc = "",
    showAnalysis = false,
    muraType: incomingMuraType = "",
    customMuraType: incomingCustomMuraType = "",
    selectedCauses: incomingCauses = [],
    selectedIssues: incomingIssues = [],
    fromLightLeakage = false,
    issueType: incomingIssueType,
    yoloAnnotations = [],
    predictions: incomingPredictions = [],
    predictedImage = null,
    sentToEmail = "",
    predictedImageUrl = "",
    uploadedImage = "",
  } = location.state || {};

  const [selectedMuraType, setSelectedMuraType] = useState("");
  const [customMuraType, setCustomMuraType] = useState("");
  const [customMuraTypes, setCustomMuraTypes] = useState({});
  const [recordedEntries, setRecordedEntries] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [muraTypes, setMuraTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCustomMuraTypeValid, setIsCustomMuraTypeValid] = useState(true);
  const uploadedImageState = globalState?.uploadedImage || uploadedImage || imageSrc || "";
  const incomingMuraTypeNormalized = incomingMuraType.toLowerCase().replace(/_/g, ' ');
  const issueType = incomingIssueType || (incomingMuraTypeNormalized === "pressing light leakage" ? "LightLeakage" : "MuraLabeling");
  const latestFormData = globalState.formData && globalState.formData.length > 0 ? globalState.formData[globalState.formData.length - 1] : null;
   const getNextMuraTypeId = () => {
  const existingIds = [
    ...muraTypes.map(type => parseInt(type.muraLabId)),
    ...Object.values(customMuraTypes).map(id => parseInt(id)),
  ];
  return Math.max(...existingIds, 0) + 1;
  };


  useEffect(() => {
    if (
      !location.state?.fromSubmission &&
      (!globalState ||
        !globalState.uploadedImage)
    ) {
      console.warn("Missing or invalid global state data:", {
        globalState,
        suggestion: globalState?.suggestion,
        predictions: globalState?.predictions,
        predictedImage: globalState?.predictedImage,
        uploadedImage: globalState?.uploadedImage,
        predictedImageUrl: globalState?.predictedImageUrl,
      });
      Swal.fire({
        icon: "warning",
        title: "Missing Image Data",
        text: "No uploaded image found. Please upload an image first.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/disagree-judgement", {
          replace: true,
          state: {
            suggestion: globalState?.suggestion || incomingSuggestion || '',
            imageSrc,
            showAnalysis,
            predictions: globalState?.predictions || incomingPredictions || [],
            uploadedImage: globalState?.uploadedImage || uploadedImageState || '',
            predictedImage: globalState?.predictedImage || predictedImage || null,
            predictedImageUrl: globalState?.predictedImageUrl || predictedImageUrl || '',
            email,
            companyInfo,
          },
        });
      });
    }
    if (!latestFormData) {
      console.warn("No product data found in global state");
      Swal.fire({
        icon: "warning",
        title: "No Product Data",
        text: "Please complete the product info input first.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/product-info-detection");
      });
    }
  }, [
    incomingSuggestion,
    incomingCauses,
    incomingIssues,
    incomingMuraType,
    incomingCustomMuraType,
    yoloAnnotations,
    incomingPredictions,
    fromLightLeakage,
    incomingIssueType,
    imageSrc,
    predictedImage,
    predictedImageUrl,
    uploadedImage,
    globalState,
    latestFormData,
    navigate,
    email,
    companyInfo,
  ]);

  useEffect(() => {
    const loadIssueTypes = async () => {
      try {
        setIsLoading(true);
        const response = await fetchIssueTypes();
        const types = response.data.map(type => ({
          muraType: type.muraType.replace(/_/g, ' '),
          muraLabId: type.muraLabId,
        }));
        setMuraTypes(types);
      } catch (err) {
        console.error('Failed to load issue types:', err.message);
        Swal.fire({
          icon: "error",
          title: "Failed to Load Issue Types",
          text: `Failed to load issue types: ${err.message}`,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadIssueTypes();
  }, []);


 useEffect(() => {
  if (!hasInitialized) {
    let initialEntries = [];
    if (location.state?.keepState) {
      initialEntries = (location.state.recordedEntries || []).filter(
        entry => entry && typeof entry.muraType === 'string' && typeof entry.rootCause === 'string'
      );
      setSelectedMuraType(location.state.selectedMuraType || "");
      setCustomMuraType(location.state.customMuraType || "");
    } else if (location.state?.recordedEntries) {
      initialEntries = location.state.recordedEntries.filter(
        entry => entry && typeof entry.muraType === 'string' && typeof entry.rootCause === 'string'
      );
    } else {
      const storedEntries = localStorage.getItem("recordedEntries");
      if (storedEntries) {
        try {
          const parsedEntries = JSON.parse(storedEntries);
          initialEntries = parsedEntries.filter(
            entry => entry && typeof entry.muraType === 'string' && typeof entry.rootCause === 'string'
          );
        } catch (e) {
          console.error("Failed to parse recordedEntries", e);
          Swal.fire({
            icon: "error",
            title: "Data Parsing Error",
            text: "Failed to parse recorded entries from storage. Clearing invalid data.",
            confirmButtonColor: "#2563eb",
            confirmButtonText: "OK",
          });
          localStorage.removeItem("recordedEntries");
        }
      }
    }
    setRecordedEntries(initialEntries);

    if (incomingMuraType) {
      setSelectedMuraType(incomingMuraType);
    } else if (incomingCustomMuraType) {
      setCustomMuraType(incomingCustomMuraType);
      setSelectedMuraType("Other");
    }

    const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");


    const newState = {
      recordedEntries: initialEntries,
      selectedMuraType,
      customMuraType,
      uploadedImage: uploadedImageState,
      muraData,
      suggestion: incomingSuggestion || globalState?.suggestion || '',
      predictions: incomingPredictions.length > 0 ? incomingPredictions : globalState?.predictions || [],
      predictedImage: predictedImage || globalState?.predictedImage || null,
      predictedImageUrl: predictedImageUrl || globalState?.predictedImageUrl || '',
    };
    updateState(newState);
    setHasInitialized(true);
  }
}, [
  incomingMuraType,
  incomingCustomMuraType,
  incomingPredictions,
  incomingSuggestion,
  predictedImage,
  predictedImageUrl,
  location.state?.keepState,
  location.state?.recordedEntries,
  uploadedImageState,
  globalState,
  selectedMuraType,
  customMuraType,
]);


useEffect(() => {
  if ((incomingMuraType || incomingCustomMuraType) && location.state?.fromRootCauseDetail) {
    if (!incomingCauses.length) {
      return;
    }
    const typeToRecord = incomingCustomMuraType || incomingMuraType;
    if (!typeToRecord || !incomingCauses[0]) {
      console.error("Invalid issue type or root cause:", { typeToRecord, rootCause: incomingCauses[0] });
      Swal.fire({
        icon: "error",
        title: "Invalid Data",
        text: "Invalid issue type or root cause provided.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    const formattedMuraType = typeToRecord.toLowerCase().replace(/\s+/g, "_");
    const formattedCause = incomingCauses[0].toLowerCase().replace(/\s+/g, "_");
    let rectangles = [];
    try {
      const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");
      let sourceRectangles = yoloAnnotations.length > 0 ? yoloAnnotations.map((anno, index) => {
        if (!Array.isArray(anno) || anno.length < 5 || !anno.every(val => Number.isFinite(val))) {
          console.warn(`Invalid yoloAnnotation at index ${index}:`, anno);
          return null;
        }
        return {
          muraType: formattedMuraType,
          x: anno[1] * (muraData.imageDimensions?.width || 1920),
          y: anno[2] * (muraData.imageDimensions?.height || 1080),
          width: anno[3] * (muraData.imageDimensions?.width || 1920),
          height: anno[4] * (muraData.imageDimensions?.height || 1080),
          annotationIndex: index,
        };
      }).filter(rect => rect !== null) : (muraData.rectangles || []).filter(rect => rect.muraType === formattedMuraType);
      const seenRectHashes = new Set();
      rectangles = sourceRectangles.filter(rect => {
        const hash = generateRectHash(rect);
        if (seenRectHashes.has(hash)) {
          console.warn("Duplicate rectangle detected:", rect);
          return false;
        }
        seenRectHashes.add(hash);
        return true;
      });
    } catch (e) {
      console.warn("Failed to parse muraData or process yoloAnnotations:", e);
    }

    const rectangleCount = rectangles.length || 1;
    let newEntries = [];
    if (fromLightLeakage) {
      for (let i = 0; i < rectangleCount; i++) {
        const yolo = yoloAnnotations[i];
        const yoloAnnotation = yolo && Array.isArray(yolo) && yolo.length >= 5 && yolo.every(val => Number.isFinite(val))
          ? {
              classId: yolo[0],
              x_center: yolo[1],
              y_center: yolo[2],
              width: yolo[3],
              height: yolo[4],
            }
          : null;
        if (!yoloAnnotation) {
          console.warn(`No valid yoloAnnotation for index ${i}:`, yolo);
        }
        newEntries.push({
          muraType: formattedMuraType,
          rootCause: formattedCause,
          rootIssues: [], 
          rectangleIndex: i,
          yoloAnnotation,
        });
      }
    } else {
      for (let i = 0; i < rectangleCount; i++) {
        const yolo = yoloAnnotations[i];
        const yoloAnnotation = yolo && Array.isArray(yolo) && yolo.length >= 5 && yolo.every(val => Number.isFinite(val))
          ? {
              classId: yolo[0],
              x_center: yolo[1],
              y_center: yolo[2],
              width: yolo[3],
              height: yolo[4],
            }
          : null;
        if (!yoloAnnotation) {
          console.warn(`No valid yoloAnnotation for index ${i}:`, yolo);
        }
        newEntries.push({
          muraType: formattedMuraType,
          rootCause: formattedCause,
          rootIssues: incomingIssues.length > 0
            ? incomingIssues.map(issue => issue.toLowerCase().replace(/\s+/g, "_"))
            : [],
          rectangleIndex: i,
          yoloAnnotation,
        });
      }
    }

    setRecordedEntries((prev) => {
      const updatedEntries = [...prev, ...newEntries];
      const newState = {
        recordedEntries: updatedEntries,
        muraData: JSON.parse(localStorage.getItem("muraData") || "{}"),
        selectedMuraType: globalState?.selectedMuraType || "",
        customMuraType: globalState?.customMuraType || "",
        suggestion: incomingSuggestion || globalState?.suggestion || '',
        predictions: incomingPredictions.length > 0 ? incomingPredictions : globalState?.predictions || [],
        predictedImage: predictedImage || globalState?.predictedImage || null,
        predictedImageUrl: predictedImageUrl || globalState?.predictedImageUrl || '',
      };
      updateState(newState);
      return updatedEntries;
    });


    const navigateState = {
      ...location.state,
      muraType: undefined,
      customMuraType: undefined,
      selectedCauses: undefined,
      selectedIssues: undefined,
      recordedEntries: [...recordedEntries, ...newEntries], 
      selectedMuraType: globalState?.selectedMuraType || "", 
      customMuraType: globalState?.customMuraType || "", 
      fromLightLeakage: undefined,
      issueType,
      yoloAnnotations,
      muraData: JSON.parse(localStorage.getItem("muraData") || "{}"),
      suggestion: incomingSuggestion || globalState?.suggestion || '',
      predictions: incomingPredictions.length > 0 ? incomingPredictions : globalState?.predictions || [],
      predictedImage: predictedImage || globalState?.predictedImage || null,
      sentToEmail,
      uploadedImage: uploadedImageState,
    };

    navigate("/Re-labeling", {
      state: navigateState,
      replace: true,
    });
  }
}, [
  incomingMuraType,
  incomingCustomMuraType,
  incomingCauses,
  incomingIssues,
  fromLightLeakage,
  yoloAnnotations,
  incomingPredictions,
  incomingSuggestion,
  predictedImage,
  location.state?.fromRootCauseDetail,
  globalState,
  uploadedImageState,
]);

  const handleCustomMuraTypeChange = (e) => {
      const value = e.target.value;
      const invalidWords = ["other", "others", "na", "unknown", "none", "etc", "nk", "undefined", "null", "blank"];
      const validInputRegex = /^[a-zA-Z\s]*$/; 

      if (!validInputRegex.test(value)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Custom Issue Type",
          text: "The issue type can only contain letters and spaces. Special characters and numbers are not allowed.",
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
        setIsCustomMuraTypeValid(false); 
        return;
      }

      if (invalidWords.includes(value.trim().toLowerCase())) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Custom Issue Type",
          text: `The issue type "${value.trim()}" is not allowed. Please enter a valid custom issue type.`,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
        setIsCustomMuraTypeValid(false); 
        return;
      }

      if (muraTypes.some(type => type.muraType.toLowerCase() === value.trim().toLowerCase())) {
        Swal.fire({
          icon: "warning",
          title: "Issue Type Exists",
          text: `The issue type "${value.trim()}" already exists in the list. Please select it from the available options.`,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
        setIsCustomMuraTypeValid(false);
        return;
      }
      setCustomMuraType(value);
      setIsCustomMuraTypeValid(true); 
      if (value.trim() && selectedMuraType !== "Other") {
        setSelectedMuraType("Other");
      }
      if (!value.trim() && selectedMuraType === "Other") {
        setSelectedMuraType("");
        setIsCustomMuraTypeValid(true);
      }
    };

  const handleCustomMuraTypeFocus = () => {
    if (selectedMuraType !== "Other") {
      setSelectedMuraType("Other");
    }
  };

  const handleMuraTypeSelect = (type) => {
    if (selectedMuraType === type) {
      setSelectedMuraType("");
      if (type === "Other") {
        setCustomMuraType("");
        updateState({ customMuraType: "" });
      }
    } else {
      setSelectedMuraType(type);
      if (type !== "Other") {
        setCustomMuraType("");
        updateState({ customMuraType: "" });
      }
    }
    updateState({ selectedMuraType: type });
  };


const handleLabeling = () => {
  if (!selectedMuraType) {


      Swal.fire({
        icon: "warning",
        title: "No Mura Type Selected",
        text: "Please select a mura type before proceeding.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
    return;
  }

  if (selectedMuraType === "Other" && !customMuraType.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Custom Mura Type",
        text: "Please specify a custom mura type.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
    return;
  }

  const muraTypeToPass = selectedMuraType === "Other" ? customMuraType : selectedMuraType;
  const muraTypeNormalized = muraTypeToPass.toLowerCase().trim().replace(/\s+/g, " ");
  const computedIssueType = muraTypeNormalized === "pressing light leakage" ? "LightLeakage" : "MuraLabeling";

  const muraData = JSON.parse(localStorage.getItem("muraData") || "{}");

  const formattedMuraType = muraTypeNormalized.replace(/\s+/g, "_");

  if (selectedMuraType === "Other") {
    setCustomMuraTypes(prev => ({
      ...prev,
      [formattedMuraType]: prev[formattedMuraType] || String(getNextMuraTypeId()),
    }));
  }
  const stateToPass = {
    muraType: selectedMuraType === "Other" ? "" : muraTypeToPass,
    customMuraType: selectedMuraType === "Other" ? customMuraType.trim() : "",
    recordedEntries,
    selectedMuraType: formattedMuraType,
    fromMuraLabeling: true,
    issueType: computedIssueType,
    yoloAnnotations,
    muraData,
    sentToEmail,
    uploadedImage: uploadedImageState,
    customMuraTypes: {
      ...muraTypes.reduce((acc, type) => ({
        ...acc,
        [type.muraType.toLowerCase().replace(/\s+/g, "_")]: type.muraLabId,
      }), {}),
      ...customMuraTypes,
    },
  };
  navigate("/MarkRelabeling", { state: stateToPass });
};
  const handleClear = async () => {
    const result = await Swal.fire({
      title: "Clear All Labelings?",
      text: "This will remove all your Mura type selections and recordings, but keep the uploaded image and predictions.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, clear it!",
    });

    if (!result.isConfirmed) return;

    setSelectedMuraType("");
    setCustomMuraType("");
    setRecordedEntries([]);

    const clearItems = [
      "recordedEntries",
      "selectedMuraType",
      "muraData",
      "muraRectangles",
    ];
    clearItems.forEach((item) => localStorage.removeItem(item));

    const newState = {
      recordedEntries: [],
      selectedMuraType: "",
      customMuraType: "",
      muraData: null,
      suggestion: globalState?.suggestion || incomingSuggestion || '',
      predictions: globalState?.predictions || incomingPredictions || [],
      uploadedImage: uploadedImageState,
      predictedImage: globalState?.predictedImage || predictedImage || null,
      sentToEmail,
      predictedImageUrl: globalState?.predictedImageUrl || predictedImageUrl || '',
    };
    updateState(newState);

    navigate("/Re-labeling", {
      replace: true,
      state: {
        issueType,
        suggestion: globalState?.suggestion || incomingSuggestion || '',
        predictions: globalState?.predictions || incomingPredictions || [],
        uploadedImage: uploadedImageState,
        predictedImage: globalState?.predictedImage || predictedImage || null,
        sentToEmail,
        predictedImageUrl: globalState?.predictedImageUrl || predictedImageUrl || '',
        customMuraTypes: {
        ...muraTypes.reduce((acc, type) => ({
          ...acc,
          [type.muraType.toLowerCase().replace(/\s+/g, "_")]: type.muraLabId,
        }), {}),
        ...customMuraTypes,
      },
      },
    });
  };

  const handleSubmit = async () => {
    if (!recordedEntries.length) {
      Swal.fire({
        icon: "error",
        title: "No Data",
        text: "Please add at least one issue type labeling before submission.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

  if (!latestFormData) {
    console.warn('Submission failed: No product data in global state');
    Swal.fire({
      icon: "error",
      title: "No Product Data",
      text: "Please complete the product info input first.",
      confirmButtonColor: "#2563eb",
      confirmButtonText: "OK",
    });
    navigate("/product-info-detection");
    return;
  }

    const validEntries = recordedEntries.filter((entry, index) => {
      if (!entry.rootCause || typeof entry.rootCause !== 'string' || entry.rootCause.trim() === '') {
        console.warn(`Invalid rootCause for recordedEntries[${index}]:`, entry.rootCause);
        return false;
      }
      return true;
    });

    if (!validEntries.length) {
      Swal.fire({
        icon: "error",
        title: "Missing Data",
        text: "All recorded entries must have a valid, non-empty root cause.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!companyInfo || typeof companyInfo !== 'string' || companyInfo.trim() === '') {
      Swal.fire({
        icon: "error",
        title: "Invalid Company Info",
        text: "Company information is missing or invalid. Please ensure a valid JWT token is provided.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Email",
        text: "A valid email address is required.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      return;
    }

    if (!globalState || !globalState.suggestion || typeof globalState.suggestion !== 'string' || globalState.suggestion.trim() === '') {
      Swal.fire({
        icon: "error",
        title: "Missing Feedback Remark",
        text: "A feedback remark is required for submission. Please provide a suggestion in Disagree Judgement.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      navigate("/disagree-judgement", { replace: true });
      return;
    }

    if (!globalState.predictions || !Array.isArray(globalState.predictions)) {
      console.warn("Predictions are missing or invalid, proceeding with empty predictions.");
      globalState.predictions = [];
    }

    setIsSubmitting(true);

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("Authentication token is missing. Please log in again.");
      }

      const muraData = globalState?.muraData || JSON.parse(localStorage.getItem("muraData") || "{}");
      if (
        !muraData.rectangles ||
        !Array.isArray(muraData.rectangles) ||
        !muraData.imageDimensions?.width ||
        !muraData.imageDimensions?.height ||
        muraData.imageDimensions.width <= 0 ||
        muraData.imageDimensions.height <= 0
      ) {
        throw new Error("muraData must include valid rectangles and positive image dimensions.");
      }
      const imgWidth = muraData.imageDimensions.width;
      const imgHeight = muraData.imageDimensions.height;
      const seenRectHashes = new Set();
      const validatedRectangles = muraData.rectangles.filter((rect, idx) => {
        if (
          !Number.isFinite(rect.x) ||
          !Number.isFinite(rect.y) ||
          !Number.isFinite(rect.width) ||
          !Number.isFinite(rect.height) ||
          rect.width <= 0 ||
          rect.height <= 0
        ) {
          console.warn(`Invalid rectangle at index ${idx}:`, rect);
          return false;
        }
        rect.x = Math.max(0, Math.min(rect.x, imgWidth - rect.width));
        rect.y = Math.max(0, Math.min(rect.y, imgHeight - rect.height));
        rect.width = Math.min(rect.width, imgWidth - rect.x);
        rect.height = Math.min(rect.height, imgHeight - rect.y);
        const hash = generateRectHash(rect);
        if (seenRectHashes.has(hash)) {
          console.warn(`Duplicate rectangle detected at index ${idx}:`, rect);
          return false;
        }
        seenRectHashes.add(hash);
        return true;
      });
      if (!validatedRectangles.length) {
        throw new Error("No valid rectangles found after validation.");
      }
      let rawImageFile;
      try {
        if (!globalState?.uploadedImage) {
          throw new Error("No uploaded image provided in global state.");
        }
        const isValidImageSource = (src) => {
          if (typeof src === 'string') {
            return src.startsWith('data:image/') || src.startsWith('http://') || src.startsWith('https://');
          }
          return src instanceof File;
        };
        if (!isValidImageSource(globalState.uploadedImage)) {
          throw new Error(`Invalid uploadedImage format: ${typeof globalState.uploadedImage}, Value: ${globalState.uploadedImage instanceof File ? globalState.uploadedImage.name : globalState.uploadedImage.slice(0, 50)}`);
        }
        if (globalState.uploadedImage instanceof File) {
          rawImageFile = globalState.uploadedImage;
        } else if (typeof globalState.uploadedImage === 'string') {
          const response = await fetch(globalState.uploadedImage);
          if (!response.ok) {
            throw new Error(`Failed to fetch image from URL: ${globalState.uploadedImage.slice(0, 50)}`);
          }
          const blob = await response.blob();
          rawImageFile = new File([blob], `raw_image_${Date.now()}.jpg`, { type: blob.type });
        }
        if (rawImageFile.size === 0) {
          throw new Error("Raw image file is empty.");
        }
      } catch (error) {
        console.error("Failed to process raw image:", {
          message: error.message,
          uploadedImage: globalState?.uploadedImage ? `Type: ${typeof globalState.uploadedImage}, Value: ${globalState.uploadedImage instanceof File ? globalState.uploadedImage.name : globalState.uploadedImage.slice(0, 50)}` : 'Missing',
        });
        Swal.fire({
          icon: "error",
          title: "Image Processing Failed",
          text: `Failed to process raw image: ${error.message}`,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
        setIsSubmitting(false);
        return;
      }
      const predictedImageUrlFromState = globalState?.predictedImageUrl || predictedImageUrl || '';
      const savedPredictions = globalState?.predictions || incomingPredictions || [];
      const formattedPredictions = savedPredictions.length > 0
        ? savedPredictions.map((pred, index) => {
            const issueId = pred.class_id?.toString() || `pred_${index}`;
            const issueType = pred.class_name || 'unknown';
            if (!issueType || typeof issueType !== 'string' || issueType.trim() === '') {
              console.warn(`predictions[${index}].issueType is invalid, setting to 'unknown'`);
            }
            if (!Number.isFinite(pred.confidence) || pred.confidence < 0 || pred.confidence > 1) {
              console.warn(`predictions[${index}].confidence invalid, set to 0`);
              pred.confidence = 0;
            }
            let bbox = pred.bbox || [0, 0, 0, 0];
            if (!Array.isArray(bbox) || bbox.length !== 4 || bbox.some(val => !Number.isFinite(val))) {
              console.warn(`predictions[${index}].bbox invalid or missing, setting to [0,0,0,0]`, pred.bbox);
              bbox = [0, 0, 0, 0];
            }
            return {
              issueId,
              issueType: issueType.trim() || 'unknown',
              rootCauseType: pred.rootCauseType || '',
              issueDetail: pred.issueDetail || '',
              confidence: pred.confidence,
              bbox,
              predictedImageUrl: index === savedPredictions.length - 1 && predictedImageUrlFromState ? predictedImageUrlFromState : '',
              coordinates: {
                x: Math.round(bbox[0] || 0),
                y: Math.round(bbox[1] || 0),
                width: Math.round((bbox[2] || 0) - (bbox[0] || 0)),
                height: Math.round((bbox[3] || 0) - (bbox[1] || 0)),
              },
            };
          })
        : [];
      const entryMap = recordedEntries.reduce((acc, entry, index) => {
        const muraTypeLower = (entry.muraType || 'unknown').toLowerCase().replace(/\s+/g, "_");
        if (!acc[muraTypeLower]) {
          acc[muraTypeLower] = [];
        }
        acc[muraTypeLower].push({ ...entry, originalIndex: index });
        return acc;
      }, {});
      const rectangleCounts = validatedRectangles.reduce((acc, rect) => {
        const muraTypeLower = (rect.muraType || customMuraType || "unknown")
          .toLowerCase()
          .replace(/\s+/g, "_");
        acc[muraTypeLower] = (acc[muraTypeLower] || 0) + 1;
        return acc;
      }, {});
      for (const [muraType, rectCount] of Object.entries(rectangleCounts)) {
        const entries = entryMap[muraType] || [];
        if (!entries.length) {
          Swal.fire({
            icon: "error",
            title: "Missing Recorded Entries",
            text: `No recorded entries found for mura type: ${muraType.replace(/_/g, ' ')}. Please add the necessary entries.`,
            confirmButtonColor: "#2563eb",
            confirmButtonText: "OK",
          });
          setIsSubmitting(false);
          return;
        }
        if (rectCount > entries.length) {
          Swal.fire({
            icon: "error",
            title: "Too Many Rectangles",
            text: `You have ${rectCount} rectangles for ${muraType.replace(/_/g, ' ')}, but only ${entries.length} recorded entries. Please add more entries or remove excess rectangles.`,
            confirmButtonColor: "#2563eb",
            confirmButtonText: "OK",
          });
          setIsSubmitting(false);
          return;
        }
      }
      const defaultMuraTypeToClassId = {
        pressing_light_leakage: '0',
        dark_state_mura: '1',
        clouding: '2',
        wavy_mura: '3',
        lamp_shades: '4',
        light_bract: '5',
        grid_mura: '6',
        corner_shadow: '7',
        bright_side_around: '8',
        dark_spot: '9',
        bright_spot: '10',
        horizontal_band: '11',
        vertical_band: '12',
        horizontal_line: '13',
        vertical_line: '14',
      };
      const muraTypeToClassId = savedPredictions.length > 0
        ? savedPredictions.reduce((acc, pred) => {
            if (pred.class_name && pred.class_id) {
              acc[pred.class_name.trim().toLowerCase().replace(/\s+/g, ' ')] = pred.class_id.toString();
            }
            return acc;
          }, { ...defaultMuraTypeToClassId })
        : { ...defaultMuraTypeToClassId };
      const customMuraTypes = globalState?.customMuraTypes || {};
      const MuraRecording = {
        muraLabeling: {
          defects: validEntries.map((entry, index) => {
            const rect = validatedRectangles[index] || {
              x: 0,
              y: 0,
              width: 0,
              height: 0,
            };
            const yolo = entry.yoloAnnotation || null;
            let bbox;
            if (
              yolo &&
              typeof yolo === 'object' &&
              Number.isFinite(yolo.x_center) &&
              Number.isFinite(yolo.y_center) &&
              Number.isFinite(yolo.width) &&
              Number.isFinite(yolo.height)
            ) {
              bbox = [yolo.x_center, yolo.y_center, yolo.width, yolo.height];
            } else {
               bbox = [
              rect.x / imgWidth,
              rect.y / imgHeight,
              rect.width / imgWidth,
              rect.height / imgHeight
            ];
              console.warn(`Invalid or missing yoloAnnotation for entry ${index}, using rectangle coordinates:`, rect);
            }
            const muraType = entry.muraType || "unknown";
            const muraTypeLower = muraType.trim().toLowerCase().replace(/\s+/g, ' ');
            let classId = entry.yoloAnnotation?.classId?.toString() || "0";
          if (classId === "0") {
            classId = customMuraTypes[muraTypeLower] || muraTypeToClassId[muraTypeLower] || "0";
          }
            return {
              muraType,
              muraLabId: classId,
              muraLabelIssue: {
                muraRootCause: entry.rootCause,
                muraRootIssue: entry.rootIssues || [],
              },
              bbox,
            };
          }),
        },
      };
      if (!MuraRecording.muraLabeling.defects.length) {
        throw new Error("MuraRecording.muraLabeling.defects must be a non-empty array.");
      }
      const feedbackRemark = globalState.suggestion?.trim() || '';
      if (!feedbackRemark) {
        throw new Error("Feedback remark is empty or missing.");
      }

    let transformedDimensions = null;
    if (latestFormData.dimensions) {
      try {
        const parseDimension = (dim) => {
          if (!dim || typeof dim !== 'string') {
            throw new Error(`Invalid dimension value: ${dim}`);
          }
          const [whole, decimal] = dim.split('.').map(Number);
          if (!Number.isFinite(whole) || !Number.isFinite(decimal)) {
            throw new Error(`Invalid dimension format: ${dim}`);
          }
          return { whole, decimal };
        };
        transformedDimensions = {
          width: parseDimension(latestFormData.dimensions.width),
          height: parseDimension(latestFormData.dimensions.height),
        };
      } catch (error) {
        console.warn('Failed to transform dimensions:', {
          message: error.message,
          dimensions: latestFormData.dimensions,
        });
        Swal.fire({
          icon: "error",
          title: "Invalid Dimensions",
          text: `Failed to process dimensions: ${error.message}. Please ensure dimensions are in the format "whole.decimal" (e.g., "708.0").`,
          confirmButtonColor: "#2563eb",
          confirmButtonText: "OK",
        });
        setIsSubmitting(false);
        return;
      }
    }

      const submissionData = {
        email,
        dataFromStatus: "Detection_disagree",
        productInfo:latestFormData
        ? {
              companyInfo:  latestFormData.companyInfo || companyInfo.trim(),
              productType: latestFormData.productType || null,
              brand: latestFormData.brand,
              modelName: latestFormData.modelName || null,
              bezel: latestFormData.bezel || null,
              dimensions: transformedDimensions,
              screenType: latestFormData.screenType || null,
              inchSize: latestFormData.inchSize || null,
            }
          : null,
        predictions: formattedPredictions,
        MuraRecording,
        feedbackRemark,
        rawimage: rawImageFile,
        sentToEmail: sentToEmail || '',
      };

      let productInfoStr, predictionsStr, muraRecordingStr;
      try {
        productInfoStr = JSON.stringify(submissionData.productInfo);
        predictionsStr = JSON.stringify(submissionData.predictions);
        muraRecordingStr = JSON.stringify(submissionData.MuraRecording);
      } catch (error) {
        throw new Error(`Failed to serialize data: ${error.message}`);
      }

      const formData = new FormData();
      formData.append('email', submissionData.email);
      formData.append('dataFromStatus', submissionData.dataFromStatus);
      formData.append('productInfo', productInfoStr);
      formData.append('predictions', predictionsStr);
      formData.append('MuraRecording', muraRecordingStr);
      formData.append('feedbackRemark', submissionData.feedbackRemark);
      formData.append('rawimage', submissionData.rawimage);
      if (submissionData.sentToEmail) {
        formData.append('sentToEmail', submissionData.sentToEmail);
      }
      updateState({ submissionData });
      let response;
      try {
        response = await submitDisagreeIssueDetection(formData);
      } catch (error) {
        console.error("API response error:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
        throw new Error(error.response?.data?.error || error.message || "Failed to submit data.");
      }
      const clearItems = [
        "recordedEntries",
        "selectedMuraType",
        "muraData",
        "muraRectangles",
        "formData",
      ];
      clearItems.forEach((item) => localStorage.removeItem(item));
      setSelectedMuraType("");
      setCustomMuraType("");
      setRecordedEntries([]);
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: response.brief_message || "Data successfully submitted.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      }).then((result) => {
        if (result.isConfirmed) {
          updateState({
            formData: [],
            uploadedImage: null,
            submissionData: null,
            recordedEntries: [],
            muraData: null,
            suggestion: null,
            predictions: [],
            predictedImage: null,
            predictedImageUrl: null,
            sentToEmail: null,
            showAnalysis: false, 
            analysisResult: null, 
          });
          navigate("/productinfodetection", {
            replace: true,
            state: { fromSubmission: true },
          });
        }
      });
    } catch (error) {
      console.error("Submission error:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.response?.data?.error || error.message || "Failed to submit data. Please try again.",
        confirmButtonColor: "#2563eb",
        confirmButtonText: "OK",
      });
      if (error.message.includes("token")) {
        navigate("/login", { state: { error: error.message } });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackNavigation = () => {
    const canvas = document.getElementById("defectCanvas");
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    const clearItems = [
      "muraRectangles",
      "muraData",
      "recordedEntries",
      "selectedMuraType",
    ];
    clearItems.forEach((item) => localStorage.removeItem(item));

    setSelectedMuraType("");
    setCustomMuraType("");
    setRecordedEntries([]);
    setIsCustomMuraTypeValid(true);

    const newState = {
      recordedEntries: [],
      selectedMuraType: "",
      customMuraType: "",
      muraData: null,
      suggestion: globalState?.suggestion || incomingSuggestion || '',
      predictions: globalState?.predictions || incomingPredictions || [],
      uploadedImage: uploadedImageState,
      predictedImage: globalState?.predictedImage || predictedImage || null,
      sentToEmail,
      predictedImageUrl: globalState?.predictedImageUrl || predictedImageUrl || '',
    };
    updateState(newState);

    navigate("/disagree-judgement", {
      state: {
        suggestion: globalState?.suggestion || incomingSuggestion || '',
        imageSrc,
        showAnalysis,
        issueType,
        predictions: globalState?.predictions || incomingPredictions || [],
        uploadedImage: uploadedImageState,
        predictedImage: globalState?.predictedImage || predictedImage || null,
        sentToEmail,
        predictedImageUrl: globalState?.predictedImageUrl || predictedImageUrl || '',
      },
      replace: true,
    });
  };

  return (
    <div className="p-3 px-4 py-1 bg-black text-white min-h-screen font-sans text-left md:text-center">
      <div className="flex items-center mb-4">
        <ArrowLeft
          className="w-6 h-6 cursor-pointer mr-4 md:absolute md:left-3"
          onClick={handleBackNavigation}
        />
        <h2 className="flex-1 text-xl sm:text-2xl text-left md:text-center font-semibold">
          ReLabeling
        </h2>
      </div>

      <div className="text-sm py-1 px-0 text-[#E0E0E0] ml-0 md:ml-4">
        Select a Mura type to identify symptoms. You will be guided to record root causes on the next page. Repeat until all defects are recorded, then hit SUBMIT.
      </div>

      <div className="mt-6 text-sm sm:text-xl font-semibold mb-4">Issue Type</div>
      {isLoading ? (
        <div className="flex justify-center items-center mb-4">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 md:gap-2 mb-4 items-center justify-start md:justify-center">
          {muraTypes.map(({ muraType }) => {
            const formattedType = muraType.toLowerCase().replace(/\s+/g, "_");
            const isRecorded = recordedEntries.some((entry) => entry.muraType === formattedType);
            const isSelected = selectedMuraType === muraType;

            let bgColor;
            if (isSelected) {
              bgColor = "bg-blue-600";
            } else if (isRecorded) {
              bgColor = "bg-blue-400";
            } else {
              bgColor = "bg-[#1E2A44]";
            }

            return (
              <button
                key={muraType}
                onClick={() => handleMuraTypeSelect(muraType)}
                className={`h-[28px] sm:h-[30px] md:h-[32px] py-1 px-3 rounded-lg cursor-pointer text-[13px] min-w-[80px] sm:min-w-[100px] md:min-w-[120px] text-center ${bgColor} ${
                  isSelected ? "ring-2 ring-white" : ""
                }`}
              >
                {muraType}
              </button>
            );
          })}
        </div>
      )}
      {isPowerAdmin && (
      <div className="flex items-center mt-2 justify-start md:justify-center">
        <span className="text-white text-sm sm:text-base inline-block py-2">Other</span>
        <input
          type="text"
          value={customMuraType}
          onChange={handleCustomMuraTypeChange}
          onFocus={handleCustomMuraTypeFocus}
          placeholder="Please specify"
          className="flex-1 p-2 min-h-[30px] ml-1 rounded-lg border-none bg-[#1E2A44] text-white text-sm box-border"
        />
      </div>
       )}

      <button
        onClick={handleLabeling}
        className={`py-2 px-3 rounded-lg text-sm w-full max-w-[150px] my-6 ml-auto block mt-3 ${
          (selectedMuraType && (selectedMuraType !== "Other" || customMuraType.trim()))
            ? "bg-blue-600 text-white cursor-pointer"
            : "bg-gray-500 text-gray-300 cursor-not-allowed"
        }`}
        disabled={!selectedMuraType || (selectedMuraType === "Other" && !customMuraType.trim())}
      >
        ReLabeling
      </button>

      <div className="mt-6 mb-2 text-sm sm:text-xl font-semibold">Re-Labeling Recording</div>

    <textarea
  value={Object.entries(
    recordedEntries.reduce((acc, entry) => {
      if (
        entry &&
        typeof entry.muraType === 'string' &&
        typeof entry.rootCause === 'string'
      ) {
        const muraType = entry.muraType.replace(/_/g, ' ');
        const rootCause = entry.rootCause.replace(/_/g, ' ');
        const key = `${muraType}:${rootCause}`;
        if (!acc[key]) {
          acc[key] = { muraType, rootCause, rootIssues: [] };
        }
        if (Array.isArray(entry.rootIssues)) {
          entry.rootIssues.forEach(issue => {
            const formattedIssue = issue.replace(/_/g, ' ');
            if (!acc[key].rootIssues.includes(formattedIssue)) {
              acc[key].rootIssues.push(formattedIssue);
            }
          });
        }
      }
      return acc;
    }, {})
  )
    .map(([_, { muraType, rootCause, rootIssues }]) => {
      const issues = rootIssues.length > 0 ? rootIssues.join(',  ') : '';
      return issues ? `${muraType}: ${  rootCause}: ${  issues}` : `${  muraType}: ${  rootCause}`;
    })
    .join('\n')}
  readOnly
  placeholder="Your recorded entries will appear here"
  className="w-full p-4 my-2 rounded-lg border-none bg-[#1E2A44] text-white text-xs md:text-sm box-border resize-none min-h-[150px]"
/>

      <div className="flex justify-between mt-10">
        <button
          onClick={handleClear}
          className={`bg-blue-600 px-4 py-1.5 rounded-md w-[70px] sm:w-[100px] md:w-[400px] lg:w-[600px] xl:w-[660px] text-sm sm:text-base ${
            recordedEntries.length > 0 ? "cursor-pointer" : "bg-gray-500 cursor-not-allowed"
          }`}
          disabled={recordedEntries.length === 0}
        >
          Clear
        </button>
        <button
          onClick={handleSubmit}
          className={`bg-blue-600 px-4 py-1.5 rounded-md w-[70px] sm:w-[100px] md:w-[400px] lg:w-[600px] xl:w-[660px] text-sm sm:text-base ${
            recordedEntries.length > 0 && !isSubmitting
              ? "cursor-pointer"
              : "bg-gray-500 cursor-not-allowed"
          }`}
          disabled={recordedEntries.length === 0 || isSubmitting}
        >
          {isSubmitting ? "Submitt..." : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default ReLabeling;